import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import React from 'react';
import { View, Text, TouchableOpacity, Modal, ScrollView } from 'react-native';
import tw from 'twrnc';

import { t } from '../../i18n/translations';

export interface GuestSignUpPromptProps {
  visible: boolean;
  onClose: () => void;
  onSignUp: () => void;
  language?: string;
  variant?: 'soft' | 'streak' | 'achievement';
}

/**
 * Soft sign-up prompt for guest users
 * Shows after 1st quiz or after achievements
 * Dismissible with a close button
 */
export const GuestSignUpPrompt: React.FC<GuestSignUpPromptProps> = ({
  visible,
  onClose,
  onSignUp,
  language = 'en',
  variant = 'soft'
}) => {
  const getContent = () => {
    switch (variant) {
      case 'streak':
        return {
          icon: '🔥' as const,
          title: t('guestMode.streakTitle', language),
          subtitle: t('guestMode.streakSubtitle', language),
          benefits: [
            t('guestMode.benefit1', language),
            t('guestMode.benefit2', language),
            t('guestMode.benefit3', language),
          ],
        };
      case 'achievement':
        return {
          icon: '🏆' as const,
          title: t('guestMode.achievementTitle', language),
          subtitle: t('guestMode.achievementSubtitle', language),
          benefits: [
            t('guestMode.benefit1', language),
            t('guestMode.benefit2', language),
            t('guestMode.benefit4', language),
          ],
        };
      default: // soft
        return {
          icon: '💾' as const,
          title: t('guestMode.saveProgressTitle', language),
          subtitle: t('guestMode.saveProgressSubtitle', language),
          benefits: [
            t('guestMode.benefit1', language),
            t('guestMode.benefit2', language),
            t('guestMode.benefit3', language),
            t('guestMode.benefit4', language),
          ],
        };
    }
  };

  const content = getContent();

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={tw`flex-1 bg-black bg-opacity-60 justify-center items-center px-6`}>
        <View style={tw`bg-[#0B0D1E] border border-[#D9A441]/60 rounded-3xl overflow-hidden w-full max-w-sm shadow-2xl max-h-[90%]`}>
          {/* Close button */}
          <TouchableOpacity
            onPress={onClose}
            style={tw`absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-[#15172B] border border-[#D9A441]/40 justify-center items-center`}
          >
            <Ionicons name="close" size={20} color="#F7EED7" />
          </TouchableOpacity>

          <ScrollView
            showsVerticalScrollIndicator={false}
            bounces={false}
          >
            {/* Header with icon */}
            <LinearGradient
              colors={['#24133A', '#101126', '#3D2A10']}
              style={tw`px-6 pt-8 pb-6`}
            >
              <View style={tw`items-center`}>
                <Text style={tw`text-6xl mb-3`}>{content.icon}</Text>
                <Text style={tw`text-[#FFE89A] text-2xl font-bold text-center`}>
                  {content.title}
                </Text>
                <Text style={tw`text-[#F7EED7] text-base opacity-90 text-center mt-2`}>
                  {content.subtitle}
                </Text>
              </View>
            </LinearGradient>

            {/* Benefits list */}
            <View style={tw`px-6 py-6`}>
              <Text style={tw`text-[#FFE89A] font-semibold mb-4 text-base`}>
                {t('guestMode.whySignUp', language)}
              </Text>
              {content.benefits.map((benefit, index) => (
                <View key={index} style={tw`flex-row items-start mb-3`}>
                  <Ionicons name="checkmark-circle" size={20} color="#F7C948" style={tw`mr-2 mt-0.5`} />
                  <Text style={tw`text-[#F7EED7]/85 flex-1 text-sm`}>{benefit}</Text>
                </View>
              ))}
            </View>

            {/* Action buttons */}
            <View style={tw`px-6 pb-6 gap-3`}>
              <TouchableOpacity
                onPress={onSignUp}
                activeOpacity={0.8}
              >
                <LinearGradient
                  colors={['#FFE89A', '#F7C948', '#D9A441']}
                  style={tw`py-4 rounded-2xl items-center`}
                >
                  <Text style={tw`text-[#24133A] font-bold text-base`}>
                    {t('guestMode.signUpButton', language)}
                  </Text>
                </LinearGradient>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={onClose}
                activeOpacity={0.8}
                style={tw`py-4 rounded-2xl items-center border border-[#D9A441]/40`}
              >
                <Text style={tw`text-[#F7EED7]/75 font-semibold text-base`}>
                  {t('guestMode.continuePlaying', language)}
                </Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};
